// Firebase Storage service for Presenter Photos

import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL,
  StorageReference,
  listAll
} from 'firebase/storage';
import { storage } from './config';

// For debugging
console.log('[FIREBASE] Storage initialized:', storage);

// Check if presenter photo exists in Firebase Storage
export const checkPresenterPhotoExists = async (): Promise<boolean> => {
  try {
    console.log('[FIREBASE] Checking if presenter photo exists in Firebase Storage');
    
    // First, try the direct path we expect
    try {
      const directRef = ref(storage, 'images/presenter-photo.jpg');
      await getDownloadURL(directRef);
      console.log('[FIREBASE] Presenter photo found at images/presenter-photo.jpg');
      return true;
    } catch (directError) {
      console.log('[FIREBASE] Presenter photo not found at images/presenter-photo.jpg:', directError);
    }
    
    // If direct path fails, check the images directory for any presenter photo
    try {
      const imagesRef = ref(storage, 'images');
      const listResult = await listAll(imagesRef);
      
      console.log('[FIREBASE] Files in images directory:', listResult.items.map(item => item.name));
      
      const presenterPhotoItem = listResult.items.find(item => 
        item.name.toLowerCase().includes('presenter') && 
        item.name.toLowerCase().includes('photo')
      );
      
      if (presenterPhotoItem) {
        console.log('[FIREBASE] Found presenter photo in images directory:', presenterPhotoItem.name);
        return true;
      }
    } catch (listError) {
      console.log('[FIREBASE] Error listing files in images directory:', listError);
    }
    
    // Check root directory as a last resort
    try {
      const rootRef = ref(storage, '');
      const rootListResult = await listAll(rootRef);
      
      console.log('[FIREBASE] Directories in root:', rootListResult.prefixes.map(prefix => prefix.name));
      console.log('[FIREBASE] Files in root:', rootListResult.items.map(item => item.name));
      
      // Check if there's a presenter-photo.jpg in the root
      const rootPresenterPhotoItem = rootListResult.items.find(item => 
        item.name.toLowerCase().includes('presenter') && 
        item.name.toLowerCase().includes('photo')
      );
      
      if (rootPresenterPhotoItem) {
        console.log('[FIREBASE] Found presenter photo in root directory:', rootPresenterPhotoItem.name);
        return true;
      }
      
      // Check if there's an images directory
      const imagesPrefix = rootListResult.prefixes.find(prefix => prefix.name === 'images');
      if (!imagesPrefix) {
        console.log('[FIREBASE] No images directory found in root');
      }
    } catch (rootListError) {
      console.log('[FIREBASE] Error listing files in root directory:', rootListError);
    }
    
    console.log('[FIREBASE] No presenter photo found in Firebase Storage');
    return false;
  } catch (error) {
    console.error('[FIREBASE] Error checking if presenter photo exists:', error);
    return false;
  }
};

// Upload a presenter photo to Firebase Storage
export const uploadPresenterPhoto = (file: File, onProgress?: (progress: number) => void) => {
  return new Promise<string>((resolve, reject) => {
    try {
      console.log('[FIREBASE] Starting upload of presenter photo');
      console.log('[FIREBASE] File:', file.name, file.type, file.size);
      
      // Always use the same filename for consistency
      const storageRef = ref(storage, `images/presenter-photo.jpg`);
      console.log('[FIREBASE] Storage reference created:', storageRef.fullPath);
      
      const uploadTask = uploadBytesResumable(storageRef, file);
      console.log('[FIREBASE] Upload task created');

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`[FIREBASE] Upload progress: ${progress.toFixed(2)}%`);
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('[FIREBASE] Error uploading presenter photo:', error);
          reject(error);
        },
        () => {
          console.log('[FIREBASE] Upload completed successfully');
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => {
              console.log('[FIREBASE] Download URL:', downloadURL);
              resolve(downloadURL);
            })
            .catch((error) => {
              console.error('[FIREBASE] Error getting download URL:', error);
              reject(error);
            });
        }
      );
    } catch (error) {
      console.error('[FIREBASE] Error setting up upload:', error);
      reject(error);
    }
  });
};

// Get the download URL for the presenter photo
export const getPresenterPhotoUrl = async (): Promise<string> => {
  try {
    console.log('[FIREBASE] Getting presenter photo URL...');
    
    // Check if presenter photo exists in Firebase Storage
    const exists = await checkPresenterPhotoExists();
    
    if (exists) {
      // First try the expected path
      try {
        console.log('[FIREBASE] Trying to get photo from expected path in Firebase Storage');
        const storageRef = ref(storage, 'images/presenter-photo.jpg');
        const url = await getDownloadURL(storageRef);
        console.log('[FIREBASE] Successfully got photo from Firebase Storage:', url);
        return url;
      } catch (directError) {
        console.log('[FIREBASE] Error getting photo from expected path:', directError);
        
        // If that fails, try to find it in the images directory
        try {
          const imagesRef = ref(storage, 'images');
          const listResult = await listAll(imagesRef);
          
          const presenterPhotoItem = listResult.items.find(item => 
            item.name.toLowerCase().includes('presenter') && 
            item.name.toLowerCase().includes('photo')
          );
          
          if (presenterPhotoItem) {
            const alternativeRef = ref(storage, `images/${presenterPhotoItem.name}`);
            const alternativeUrl = await getDownloadURL(alternativeRef);
            console.log('[FIREBASE] Found alternative presenter photo:', alternativeUrl);
            return alternativeUrl;
          }
        } catch (listError) {
          console.log('[FIREBASE] Error finding alternative presenter photo:', listError);
        }
      }
    }
    
    // If Firebase Storage fails or photo doesn't exist, fall back to the local copy
    console.log('[FIREBASE] Falling back to local image');
    const localPath = '/assets/images/presenter-photo.jpg';
    console.log(`[FIREBASE] Using local image path: ${localPath}`);
    return localPath;
  } catch (error) {
    console.error('[FIREBASE] Error getting presenter photo URL:', error);
    throw error;
  }
};
